import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Select,
  SkeletonBodyText,
  Banner,
  Divider,
  ChoiceList,
} from '@shopify/polaris';
import { useShopSettings, useUpdateConsent } from '../hooks/api';
import { useTranslation, useLanguage, type Language } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';

export function Settings() {
  const { data: settings, isLoading, error } = useShopSettings();
  const updateConsentMutation = useUpdateConsent();
  const t = useTranslation();
  const { language, setLanguage } = useLanguage();

  const [consent, setConsent] = useState({
    adStorage: 'UNSPECIFIED',
    analyticsStorage: 'UNSPECIFIED',
    adUserData: 'UNSPECIFIED',
    adPersonalization: 'UNSPECIFIED',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Sync consent state when async settings data arrives (skip if user is editing)
  useEffect(() => {
    if (!isDirty && settings?.defaultConsent) {
      setConsent({
        adStorage: settings.defaultConsent.adStorage || 'UNSPECIFIED',
        analyticsStorage: settings.defaultConsent.analyticsStorage || 'UNSPECIFIED',
        adUserData: settings.defaultConsent.adUserData || 'UNSPECIFIED',
        adPersonalization: settings.defaultConsent.adPersonalization || 'UNSPECIFIED',
      });
    }
  }, [settings?.defaultConsent, isDirty]);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleConsentChange = useCallback(
    (field: keyof typeof consent) => (value: string[]) => {
      setIsDirty(true);
      setConsent((prev) => ({ ...prev, [field]: value[0] }));
    },
    []
  );

  const handleSaveConsent = useCallback(async () => {
    setSaveSuccess(false);
    setSaveError(null);
    try {
      await updateConsentMutation.mutateAsync(consent);
      setIsDirty(false);
      setSaveSuccess(true);
    } catch (error: any) {
      setSaveError(error.message || t.settings.saveFailed);
    }
  }, [consent, updateConsentMutation, t]);

  const handleLanguageChange = useCallback(
    (value: string) => {
      setLanguage(value as Language);
    },
    [setLanguage]
  );

  if (isLoading) {
    return (
      <Page title={t.settings.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title={t.settings.title}>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>{t.common.error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // These MUST be the wire values the API speaks. GET /settings returns the
  // Prisma enum ('GRANTED' / 'DENIED') and POST /consent validates against the
  // same set, but these were lowercase: no radio ever matched, so the panel
  // could not show the merchant their own consent defaults, and saving one
  // posted 'granted' and came back 400 Invalid adStorage value. The Consent
  // Mode v2 panel of a consent product was inert in both directions.
  const consentOptions = [
    { value: 'GRANTED', label: t.settings.granted },
    { value: 'DENIED', label: t.settings.denied },
    { value: 'UNSPECIFIED', label: t.settings.unspecified },
  ];

  return (
    <Page title={t.settings.title} subtitle={t.settings.subtitle}>
      <Layout>
        {/* Success/Error Banner */}
        {saveSuccess && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSaveSuccess(false)}>
              <p>{t.common.success}</p>
            </Banner>
          </Layout.Section>
        )}
        {saveError && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setSaveError(null)}>
              <p>{saveError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Language Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.settings.language}
              </Text>
              <Select
                label={t.settings.language}
                labelHidden
                options={[
                  { value: 'fr', label: t.settings.french },
                  { value: 'en', label: t.settings.english },
                  { value: 'de', label: t.settings.german },
                  { value: 'es', label: t.settings.spanish },
                  { value: 'pt-BR', label: t.settings.portugueseBR },
                  { value: 'ja', label: t.settings.japanese },
                  { value: 'nl', label: t.settings.dutch },
                ]}
                value={language}
                onChange={handleLanguageChange}
                helpText={t.settings.languageHelp}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Shop Information */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.settings.shopInfo}
              </Text>
              <BlockStack gap="200">
                <InlineStack gap="200">
                  <Text as="span" variant="bodyMd" tone="subdued">
                    {t.settings.shopDomain}:
                  </Text>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {settings?.shopDomain}
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span" variant="bodyMd" tone="subdued">
                    {t.settings.shopName}:
                  </Text>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {settings?.shopName}
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text as="span" variant="bodyMd" tone="subdued">
                    {t.settings.shopId}:
                  </Text>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {settings?.id}
                  </Text>
                </InlineStack>
                {settings?.createdAt && (
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      {t.settings.createdAt}:
                    </Text>
                    <Text as="span" variant="bodyMd">
                      {new Date(settings.createdAt).toLocaleDateString(
                        localeMap[language]
                      )}
                    </Text>
                  </InlineStack>
                )}
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Consent Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.settings.consentSettings}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {t.settings.consentDesc}
              </Text>

              <Divider />

              <Text as="h3" variant="headingSm">
                {t.settings.defaultConsent}
              </Text>

              <BlockStack gap="400">
                <ChoiceList
                  title={t.settings.adStorage}
                  choices={consentOptions}
                  selected={[consent.adStorage]}
                  onChange={handleConsentChange('adStorage')}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  {t.settings.adStorageHelp}
                </Text>

                <ChoiceList
                  title={t.settings.analyticsStorage}
                  choices={consentOptions}
                  selected={[consent.analyticsStorage]}
                  onChange={handleConsentChange('analyticsStorage')}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  {t.settings.analyticsStorageHelp}
                </Text>

                <ChoiceList
                  title={t.settings.adUserData}
                  choices={consentOptions}
                  selected={[consent.adUserData]}
                  onChange={handleConsentChange('adUserData')}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  {t.settings.adUserDataHelp}
                </Text>

                <ChoiceList
                  title={t.settings.adPersonalization}
                  choices={consentOptions}
                  selected={[consent.adPersonalization]}
                  onChange={handleConsentChange('adPersonalization')}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  {t.settings.adPersonalizationHelp}
                </Text>
              </BlockStack>

              <Divider />

              <Button
                variant="primary"
                onClick={handleSaveConsent}
                loading={updateConsentMutation.isPending}
              >
                {t.settings.saveConsent}
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
